import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useCountryNews } from '@/hooks/useCountryNews';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Newspaper, Star, Calendar } from 'lucide-react';
import { format } from 'date-fns';

// Country code to name mapping
const countryNames: Record<string, string> = {
  us: 'United States',
  ca: 'Canada',
  gb: 'United Kingdom',
  mx: 'Mexico',
  au: 'Australia',
  de: 'Germany',
  fr: 'France',
  es: 'Spain',
  br: 'Brazil',
  jp: 'Japan',
  ru: 'Russia',
  cn: 'China',
};

const flagEmojis: Record<string, string> = {
  us: '🇺🇸',
  ca: '🇨🇦',
  gb: '🇬🇧',
  mx: '🇲🇽',
  au: '🇦🇺',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  br: '🇧🇷',
  jp: '🇯🇵',
  ru: '🇷🇺',
  cn: '🇨🇳',
};

const CountryNews = () => {
  const { countrySlug } = useParams<{ countrySlug: string }>();
  const { user: supabaseUser, loading: supabaseLoading, initialCheckComplete: supabaseInitialCheck } = useAuth();
  const { user: internalUser, loading: internalLoading, initialCheckComplete: internalInitialCheck } = useInternalAuth();
  const isMobile = useIsMobile();

  const countryCode = countrySlug?.toUpperCase() || 'US';
  const countryName = countryNames[countrySlug?.toLowerCase() || 'us'] || 'United States';
  const flagEmoji = flagEmojis[countrySlug?.toLowerCase() || 'us'] || '🏳️';

  const { articles, featuredArticles, regularArticles, loading: newsLoading, error } = useCountryNews({
    country: countryName,
    limit: 30,
  });

  // Wait for both auth systems to complete their initial checks
  const bothSystemsReady = supabaseInitialCheck && internalInitialCheck;
  const anySystemLoading = supabaseLoading || internalLoading;

  // Show loading only if we haven't completed initial checks
  if (!bothSystemsReady || anySystemLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  const isAuthenticated = !!(supabaseUser || internalUser);

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-background w-full">
      {/* Header */}
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 pb-20 px-4' 
          : 'mt-24 mb-6 py-6 px-4 md:px-6'
      }`}>
        <div className="w-full max-w-4xl mx-auto space-y-6">
          {/* Back Button */}
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/home">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          {/* Country Header */}
          <div className="text-center py-8 bg-gradient-to-br from-card to-muted/30 rounded-xl border border-border">
            <span className="text-6xl mb-4 block">{flagEmoji}</span>
            <h1 className="text-3xl font-bold text-foreground">{countryName}</h1>
            <p className="text-muted-foreground mt-2">Latest sports news and updates</p>
          </div>

          {/* Featured News */}
          {newsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-48 w-full rounded-xl" />
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : error ? (
            <Card className="bg-card border-border">
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">Unable to load news. Please try again later.</p>
              </CardContent>
            </Card>
          ) : articles.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <Newspaper className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No News Yet</h3>
                <p className="text-muted-foreground">
                  There are no news articles for {countryName} at this time.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Check back soon for updates!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Featured Articles */}
              {featuredArticles.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    Featured
                  </h2>
                  {featuredArticles.map(article => (
                    <Card key={article.id} className="bg-gradient-to-br from-primary/5 to-card border-primary/20 overflow-hidden">
                      {article.imageUrl && (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={article.imageUrl} 
                            alt={article.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-primary/20 text-primary">Featured</Badge>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(article.publishedAt), 'MMM d, yyyy')}
                          </span>
                        </div>
                        <CardTitle className="text-foreground">{article.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {article.summary && (
                          <p className="text-muted-foreground">{article.summary}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Regular Articles */}
              {regularArticles.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <Newspaper className="h-5 w-5 text-primary" />
                    Latest News
                  </h2>
                  {regularArticles.map(article => (
                    <Card key={article.id} className="bg-card border-border hover:bg-muted/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {article.imageUrl && (
                            <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                              <img 
                                src={article.imageUrl} 
                                alt={article.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <span className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(article.publishedAt), 'MMM d, yyyy')}
                            </span>
                            <h3 className="font-semibold text-foreground mb-1">{article.title}</h3>
                            {article.summary && (
                              <p className="text-sm text-muted-foreground line-clamp-2">{article.summary}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Footer */}
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default CountryNews;
